import { t } from '../i18n/index.js';

/** Preserve fetch's authentication/proxy behavior in the native shell. Web
 * uploads use XHR because fetch does not expose uploaded-byte progress.
 * `init.signal` cancels the request; the report gets `uploaded: true` once
 * every byte was sent (from then on the server may already be applying it),
 * and `tracked: false` when upload progress can't be observed at all. An
 * error with `network: true` means the server could not be reached. */
export async function uploadWithProgress(url, init, report = () => {}) {
  if (typeof XMLHttpRequest === 'undefined' || globalThis.window?.__TAURI_INTERNALS__) {
    report({ label: t('vault.transfer.uploading'), tracked: false });
    return fetch(url, init).catch((error) => {
      if (error?.name !== 'AbortError') error.network = true;
      throw error;
    });
  }
  init.signal?.throwIfAborted?.();
  const resolved = new URL(url, window.location.href);
  if (resolved.origin !== window.location.origin || !resolved.pathname.startsWith('/api/')) {
    throw new Error('File upload requires a same-origin API endpoint');
  }
  const { getCsrfTokenFromCookie } = await import('../../engine/auth/handlers.js');
  const headers = new Headers(init.headers);
  const token = getCsrfTokenFromCookie();
  if (token) headers.set('X-CSRF-Token', token);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method || 'POST', resolved.href);
    xhr.withCredentials = init.credentials === 'include';
    headers.forEach((value, key) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = event => report({ label: t('vault.transfer.uploading'), current: event.loaded,
      total: event.lengthComputable ? event.total : undefined, unit: 'bytes' });
    xhr.upload.onload = () => report({ label: t('vault.transfer.waitingServer'), uploaded: true });
    xhr.onload = () => {
      if (!xhr.status) { reject(networkError(t('vault.transfer.connectionFailed'))); return; }
      resolve(new Response([204, 205, 304].includes(xhr.status) ? null : xhr.responseText, { status: xhr.status,
        headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json' } }));
    };
    xhr.onerror = () => reject(networkError(t('vault.transfer.connectionFailedRetry')));
    xhr.onabort = () => reject(init.signal?.aborted
      ? new DOMException(t('vault.transfer.cancelled'), 'AbortError')
      : new Error(t('vault.transfer.interrupted')));
    init.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(init.body);
  });
}

function networkError(message) {
  const error = new Error(message);
  error.network = true;
  return error;
}

/** A Content-Length is optional (and may describe compressed bytes). Only
 * show a percentage when it describes the bytes the stream exposes. */
export async function readResponseWithProgress(response, report = () => {}, maxBytes = Infinity) {
  const length = Number(response.headers.get('Content-Length'));
  const total = !response.headers.get('Content-Encoding') && length > 0 ? length : undefined;
  if (total > maxBytes) throw new Error(t('vault.transfer.tooLarge'));
  if (!response.body?.getReader) {
    report({ label: t('vault.transfer.downloading') });
    const bytes = await response.arrayBuffer();
    if (bytes.byteLength > maxBytes) throw new Error(t('vault.transfer.tooLarge'));
    return bytes;
  }
  const reader = response.body.getReader();
  const chunks = [];
  let size = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.length;
      if (size > maxBytes) throw new Error(t('vault.transfer.tooLarge'));
      chunks.push(value);
      report({ label: t('vault.transfer.downloading'), current: size, total, unit: 'bytes' });
    }
  } catch (error) { await reader.cancel().catch(() => {}); throw error; }
  finally { reader.releaseLock(); }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
  return bytes.buffer;
}
